import type {GroupConfig, E123Config, PlanConfig} from './types';
import {groupOffersMedical, groupRequiresAccessCode} from './types';
import {isOtherSitePageHref, sitePageOf} from './siteLinks';

function productPlans(config: GroupConfig, planIds: string[]): PlanConfig[] {
  const planById = new Map(config.plans.map((p) => [p.id, p]));
  return planIds.map((id) => planById.get(id)).filter((p): p is PlanConfig => !!p);
}

// mm/dd/yyyy that's also a real calendar date — the round-trip rejects 02/31, 13/01, etc.
function isValidMmDdYyyy(v: string): boolean {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
  if (!m) return false;
  const mm = +m[1];
  const dd = +m[2];
  const yyyy = +m[3];
  const d = new Date(yyyy, mm - 1, dd);
  return d.getMonth() === mm - 1 && d.getDate() === dd && d.getFullYear() === yyyy;
}

// Hard-stops for publishing — shape is enforced by the schema; these catch drafts that
// are valid but not launch-ready. Returns human-readable blockers (empty = publishable).
export function collectPublishBlockers(config: GroupConfig, e123: E123Config): string[] {
  const blockers: string[] = [];

  if (!config.name?.trim()) blockers.push('Add a site name (Brand & identity).');
  if (!config.primaryColor?.trim()) blockers.push('Choose a primary color (Brand & identity).');
  if (!config.supportPhone?.trim()) blockers.push('Add a support phone (Support & coverage).');

  if (!config.products.some((p) => p.category === 'medical' || p.category === 'limited-medical')) {
    blockers.push('Add a Medical or Limited medical product (Products & plans).');
  }
  for (const product of config.products) {
    const plans = productPlans(config, product.planIds);
    if (plans.length === 0) {
      blockers.push(`“${product.label}” has no plans (Products & plans).`);
    }
    for (const plan of plans) {
      if (!plan.name?.trim()) {
        blockers.push(`A plan under “${product.label}” is missing a name (Products & plans).`);
      }
    }
  }

  // e123 mapping — a published site accepts real submissions, so it must be able to map them.
  // A demo site never submits to e123, so the mapping is irrelevant and isn't required to publish.
  if (!config.isDemo) {
    if (!e123.effectiveDate?.trim()) {
      blockers.push('Set the coverage effective date (Submission mapping).');
    } else if (!isValidMmDdYyyy(e123.effectiveDate.trim())) {
      blockers.push('Use a valid mm/dd/yyyy coverage effective date (Submission mapping).');
    }
    // Code-gated sites resolve the agent per location; a public site has only this one.
    if (!groupRequiresAccessCode(config) && !e123.agentId?.trim()) {
      blockers.push('Set the e123 agent ID (Submission mapping).');
    }
    for (const plan of config.plans) {
      if (e123.planPdid[plan.id] === undefined) {
        blockers.push(`Plan “${plan.name || plan.id}” isn’t mapped to e123 (Submission mapping).`);
      }
    }
    // A recorded declination maps to a decline PDID; without it the declination is silently dropped.
    // Limited-medical-only sites never send one.
    const recordsDecline = config.products.some(
      (p) => p.declinable !== false && p.declineStyle !== 'skipped',
    );
    if (groupOffersMedical(config) && recordsDecline && e123.declinePdid === undefined) {
      blockers.push('Map the declination PDID (Submission mapping).');
    }
  }

  return blockers;
}

// Advisory recommendations — the site still publishes. `activeLocationCount` comes from
// the DB (not in the render config), so the caller supplies it.
export function collectPublishWarnings(
  config: GroupConfig,
  ctx: {activeLocationCount: number},
): string[] {
  const warnings: string[] = [];

  if (!config.coveragePeriod?.trim()) {
    warnings.push('No coverage period set — the landing page won’t show coverage dates (Support & coverage).');
  }
  if (groupRequiresAccessCode(config) && ctx.activeLocationCount === 0) {
    warnings.push('No active access codes yet — members can’t enter until you add at least one (Access codes).');
  }
  for (const product of config.products) {
    for (const plan of productPlans(config, product.planIds)) {
      const rates = Object.values(plan.rates ?? {});
      if (rates.length > 0 && rates.every((r) => !r)) {
        warnings.push(`“${plan.name || 'A plan'}” has all rates at $0 — confirm that’s intended (Products & plans).`);
      }
    }
  }
  // Landing cards that point at an empty page, or at another site's page.
  for (const item of config.resourcesHidden ? [] : (config.resourcesSection?.items ?? [])) {
    const page = sitePageOf(item.href, config.slug);
    const target = page === 'resources' ? config.resourcesPage : page === 'notices' ? config.noticesPage : undefined;
    if (page && !target?.documents.length) {
      const name = page === 'resources' ? 'Plan resources' : 'Annual notices';
      warnings.push(`“${item.title || 'A card'}” links to the ${name} page, which has no documents yet (Resources).`);
    }
    if (isOtherSitePageHref(item.href, config.slug)) {
      warnings.push(`“${item.title || 'A card'}” links to another site’s page (Resources).`);
    }
    if (!item.title.trim()) warnings.push('A landing card has no title (Resources).');
    if (!item.href.trim()) warnings.push(`“${item.title || 'A card'}” has no link yet (Resources).`);
  }
  // Pages with content that no landing card leads to (reachable only by direct URL).
  if (!config.resourcesHidden) {
    const cards = config.resourcesSection?.items ?? [];
    const pages = [
      {page: 'resources', name: 'Plan resources', content: config.resourcesPage},
      {page: 'notices', name: 'Annual notices', content: config.noticesPage},
    ] as const;
    for (const {page, name, content} of pages) {
      if (content && !cards.some((c) => sitePageOf(c.href, config.slug) === page)) {
        warnings.push(`The ${name} page has content but no landing card links to it (Resources).`);
      }
      for (const doc of content?.documents ?? []) {
        if (!doc.title.trim()) warnings.push(`A document on the ${name} page has no title (Resources).`);
        if (!doc.href.trim()) {
          warnings.push(`“${doc.title || 'A document'}” on the ${name} page has no link yet (Resources).`);
        }
      }
    }
  }
  const hasResources =
    !!config.resourcesPage || !!config.resourcesSection || (config.faq?.length ?? 0) > 0;
  if (!hasResources) {
    warnings.push('No plan resources or FAQ — members won’t have documents to review (Landing / Confirmation).');
  }

  return warnings;
}
